import axios from 'axios';
import React, { useState } from 'react';
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth0 } from '@auth0/auth0-react';

interface Tournament {
  tournament_id?: string;
  tournament_name: string;
  start_date: Date;
  end_date?: Date;
  tournament_team_enabled: boolean;
  region?: string;
}

// Regions group majors together for the Insights "Titles" widget.
const REGIONS = ['North', 'East', 'West', 'Tremblant'];


interface TournamentFormProps {
  onTournamentCreated: () => void;
  handleClose: () => void;
}

const TournamentForm: React.FC<TournamentFormProps> = ({ onTournamentCreated, handleClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const APIURL = process.env.REACT_APP_API_URL;

  const [tournament, setTournament] = useState<Tournament>({
    tournament_name: '',
    start_date: new Date(),
    end_date: new Date(),
    tournament_team_enabled: false,
    region: '',
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setTournament({
      ...tournament,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleStartDateChange = (date: Date) => {
    setTournament({ ...tournament, start_date: date });
  };

  const handleEndDateChange = (date: Date) => {
    setTournament({ ...tournament, end_date: date });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload = {
      tournament_name: tournament.tournament_name,
      start_date: tournament.start_date.toISOString().split('T')[0],
      end_date: tournament.end_date ? tournament.end_date.toISOString().split('T')[0] : null,
      tournament_team_enabled: tournament.tournament_team_enabled,
      tournament_status: 'open',
      region: tournament.region || null,
    };

    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${APIURL}/createTournaments`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      alert('Tournament created successfully!');
      setTournament({
        tournament_name: '',
        start_date: new Date(),
        end_date: new Date(),
        tournament_team_enabled: false,
      });
      onTournamentCreated();
      handleClose();
    } catch (error) {
      console.error('There was an error creating the tournament:', error);
      alert('Failed to create tournament. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'grid', gap: 16 }}>
        <div className="bga-field">
          <label className="bga-label">Tournament name</label>
          <input
            className="bga-input"
            type="text"
            name="tournament_name"
            placeholder="e.g. Summer Classic"
            value={tournament.tournament_name}
            onChange={handleInputChange}
            required
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          <div className="bga-field">
            <label className="bga-label">Start date</label>
            <DatePicker
              wrapperClassName="bga-datepicker-wrap"
              className="bga-input"
              selected={tournament.start_date}
              onChange={handleStartDateChange}
              dateFormat="yyyy-MM-dd"
              required
            />
          </div>
          <div className="bga-field">
            <label className="bga-label">End date</label>
            <DatePicker
              wrapperClassName="bga-datepicker-wrap"
              className="bga-input"
              selected={tournament.end_date}
              onChange={handleEndDateChange}
              dateFormat="yyyy-MM-dd"
            />
          </div>
          <div className="bga-field">
            <label className="bga-label">Region</label>
            <select
              className="bga-input"
              value={tournament.region || ''}
              onChange={(e) => setTournament({ ...tournament, region: e.target.value })}
            >
              <option value="">— None —</option>
              {REGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <span className="bga-muted" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
              Groups this major for the Titles leaderboard.
            </span>
          </div>
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            border: '1px solid var(--bga-border)',
            borderRadius: 10,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            name="tournament_team_enabled"
            checked={tournament.tournament_team_enabled}
            onChange={handleInputChange}
            style={{ width: 18, height: 18, cursor: 'pointer' }}
          />
          <span>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Team competition</span>
            <span className="bga-muted" style={{ display: 'block', fontSize: 12.5 }}>
              Enable automatic team play scoring for this tournament. (Most majors instead tag team
              points per player on the Majors page.)
            </span>
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
        <button type="button" className="bga-btn bga-btn-secondary" onClick={handleClose}>
          Cancel
        </button>
        <button type="submit" className="bga-btn bga-btn-primary">
          Create Tournament
        </button>
      </div>
    </form>
  );
};

export default TournamentForm;
